import type { ExchangeCourse, Invite, InviteState, Invites, Side, InviteAction } from "../types";

const LIVE_ROOM = new Set(["open", "paused", "prewarm"]);

/** 교류 룸이 지금 열려 있나(초대에 roomId 가 있고 룸 상태가 닫힘이 아님) */
export const roomLive = (inv: Invite) =>
  !!inv.roomId && (inv.roomState === null || LIVE_ROOM.has(inv.roomState));

export function actionsOf(inv: Invite, side: Side): InviteAction[] {
  if (inv.state === "pending") return side === "received" ? ["accept", "decline"] : ["cancel"];
  if (inv.state === "accepted") {
    if (roomLive(inv)) return ["console"];
    return side === "sent" ? ["open"] : [];
  }
  return [];
}

/** 손이 필요한 순서 — 대기(받은 → 보낸) · 수락(룸 열림 → 아직) · 끝난 상태는 최근 것부터 */
const RANK: Record<InviteState, number> = {
  pending: 0,
  accepted: 1,
  declined: 2,
  expired: 2,
  cancelled: 2,
};
const at = (inv: Invite) => Date.parse(inv.respondedAt ?? inv.createdAt ?? "") || 0;

export function sortInvites(xs: readonly Invite[]): Invite[] {
  return [...xs].sort(
    (a, b) =>
      RANK[a.state] - RANK[b.state] ||
      Number(roomLive(b)) - Number(roomLive(a)) ||
      at(b) - at(a) ||
      a.inviteId.localeCompare(b.inviteId),
  );
}

/** 상대 반(보낸 초대는 받는 반 · 받은 초대는 보낸 반) */
export const partnerOf = (inv: Invite, side: Side) =>
  side === "sent" ? inv.toCourse : inv.fromCourse;

/** 반 목록에서 이미 대기·수락 중인 초대가 있는 반(새 초대를 막는다 — 서버 409 와 같은 규칙) */
export function busyCourses(inv: Invites): Map<string, InviteState> {
  const m = new Map<string, InviteState>();
  for (const [side, xs] of [
    ["sent", inv.sent],
    ["received", inv.received],
  ] as const)
    for (const x of xs) {
      if (x.state !== "pending" && !(x.state === "accepted" && (roomLive(x) || !x.roomId)))
        continue;
      const id = partnerOf(x, side).courseId;
      if (!m.has(id) || x.state === "pending") m.set(id, x.state);
    }
  return m;
}

export const countPending = (inv: Invites) => ({
  received: inv.received.filter((x) => x.state === "pending").length,
  sent: inv.sent.filter((x) => x.state === "pending").length,
});

/** 코스 목록 정렬 — 라벨 순(최근 사용 순은 서버가 준 대로 두지 않고 찾기 쉽게) */
export const sortCourses = (xs: readonly ExchangeCourse[]) =>
  [...xs].sort((a, b) => a.label.localeCompare(b.label, "ko", { numeric: true }));

/** datetime-local 값(현지 시각) → ISO · 비었거나 잘못되면 null */
export function localToIso(v: string): string | null {
  if (!v) return null;
  const ms = Date.parse(v);
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
}
